import { z } from 'zod';

/**
 * Feature flags that are NOT module-backed. These are for in-progress work
 * and are resolved via the org features array or environment variables.
 *
 * For module-backed features, use module keys with `isModuleEnabled` instead.
 */
export const featureFlags = [
  'appetite_links',
  'attach_file_custom_field',
  'attestation_improvements',
  'cds_enterprise_risk',
  'conditional_fields',
  'custom_attribute_alternate_values',
  'dashboard_user_group_filter',
  'day_attestations',
  'empty_default_dashboard',
  'form_sections',
  'issue-allica',
  'issue-gc',
  'pdf_export',
  'policy_auto_draft',
  'scoring_settings_data',
  'trpc',
  'viewers',
  'statuses',
  'statuses_risk',
  'statuses_control',
  'statuses_operational',
  'statuses_compliance',
  'statuses_platform_surfaces',
] as const;

export const featureFlagSchema = z.enum(featureFlags);
export type FeatureFlag = z.infer<typeof featureFlagSchema>;

export interface Module {
  enabled: boolean;
  config?: Record<string, unknown>;
  subModules?: Record<string, Module>;
  allowTabConfig?: boolean;
}

export type ModuleConfig = Record<string, Module>;

/**
 * Extracts all valid dot-notation module key paths from a module tree type.
 * E.g. 'risk' | 'risk.subModules.impact' | 'document.subModules.attestation' | ...
 */
export type ExtractModuleKeys<
  T extends Record<string, Module>,
  Prefix extends string = '',
> = {
  [K in keyof T & string]:
    | `${Prefix}${K}`
    | (T[K] extends {
        readonly subModules: infer S extends Record<string, Module>;
      }
        ? ExtractModuleKeys<S, `${Prefix}${K}.subModules.`>
        : never);
}[keyof T & string];
